/**
 * Tech Radar Builder Types
 * Track technology adoption decisions: Adopt, Trial, Assess, Hold
 */

export type RadarRing = 'adopt' | 'trial' | 'assess' | 'hold';

export type RadarQuadrant =
  | 'techniques'
  | 'platforms'
  | 'tools'
  | 'languages_frameworks';

export interface RadarBlip {
  id: string;
  name: string;
  quadrant: RadarQuadrant;
  ring: RadarRing;
  description?: string;
  rationale?: string;
  isNew?: boolean;
  movedFrom?: RadarRing;
  tags?: string[];
  owner?: string;
  reviewDate?: string;
  links?: { label: string; url: string }[];
}

export interface TechRadar {
  id: string;
  name: string;
  description?: string;
  blips: RadarBlip[];
  settings: RadarSettings;
  created_at: string;
  updated_at: string;
}

export interface RadarSettings {
  companyName?: string;
  version?: string;
  publishDate?: string;
}

// Configuration
export const RING_CONFIG: Record<
  RadarRing,
  {
    label: string;
    description: string;
    color: string;
    order: number;
  }
> = {
  adopt: {
    label: 'Adopt',
    description: 'Technologies we have high confidence in to serve our purpose, also at scale.',
    color: '#22c55e', // green-500
    order: 0,
  },
  trial: {
    label: 'Trial',
    description: 'Worth pursuing. Important to understand how to build up this capability.',
    color: '#3b82f6', // blue-500
    order: 1,
  },
  assess: {
    label: 'Assess',
    description: 'Worth exploring with the goal of understanding how it will affect your enterprise.',
    color: '#f59e0b', // amber-500
    order: 2,
  },
  hold: {
    label: 'Hold',
    description: 'Proceed with caution. Not recommended for new projects.',
    color: '#ef4444', // red-500
    order: 3,
  },
};

export const QUADRANT_CONFIG: Record<
  RadarQuadrant,
  {
    label: string;
    description: string;
    icon: string;
    angle: number; // Starting angle in degrees
  }
> = {
  techniques: {
    label: 'Techniques',
    description: 'Ways of developing software, such as practices and methodologies.',
    icon: '🔧',
    angle: 0,
  },
  platforms: {
    label: 'Platforms',
    description: 'Things we build software on: cloud providers, databases, infrastructure.',
    icon: '☁️',
    angle: 90,
  },
  tools: {
    label: 'Tools',
    description: 'Development, testing, deployment, and collaboration tools.',
    icon: '🛠️',
    angle: 180,
  },
  languages_frameworks: {
    label: 'Languages & Frameworks',
    description: 'Programming languages, libraries, and frameworks.',
    icon: '💻',
    angle: 270,
  },
};

// Generate a unique blip ID
export function generateBlipId(): string {
  return `blip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Generate a unique radar ID
export function generateRadarId(): string {
  return `radar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
